import {
  DEFAULT_CHANNEL_NAME,
  backoffAfter,
  createAppUpdater,
  resolveRetry
} from "./chunk-VK56FKEY.mjs";

// src/react/decide.ts
function decide(input) {
  if (input.reloading) return { phase: "reloading", reload: false };
  if (input.update === "none") return { phase: "none", reload: false };
  if (input.update === "urgent") return decideUrgent(input);
  if (input.confirmOpen) {
    return input.guardsActive ? { phase: "confirm", reload: false } : { phase: "reloading", reload: true };
  }
  if (input.guardsActive) return { phase: input.dismissed ? "dismissed" : "guarded", reload: false };
  if (input.autoReloadSpent) return { phase: input.dismissed ? "dismissed" : "available", reload: false };
  if (input.hidden) return { phase: "reloading", reload: true };
  if (input.idle) {
    return input.countdownDone ? { phase: "reloading", reload: true } : { phase: "countdown", reload: false };
  }
  return { phase: input.dismissed ? "dismissed" : "available", reload: false };
}
function decideUrgent(input) {
  const blocking = input.deadlinePassed && input.guardsActive && !input.hidden && !input.autoReloadSpent && !input.keepEditing;
  if (blocking) return { phase: "confirm", reload: false, block: true };
  if (input.confirmOpen) {
    return input.guardsActive ? { phase: "confirm", reload: false } : { phase: "reloading", reload: true };
  }
  if (input.guardsActive) return { phase: "urgent", reload: false };
  if (input.autoReloadSpent) return { phase: "urgent", reload: false };
  if (input.hidden || input.deadlinePassed) return { phase: "reloading", reload: true };
  return { phase: "urgent", reload: false };
}

// src/react/idle.ts
var ACTIVITY_EVENTS = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart", "scroll"];
var THROTTLE_MS = 200;
function createIdleTracker({ target, timeoutMs, onChange }) {
  let idle = false;
  let destroyed = false;
  let timer;
  let lastActivity = -Infinity;
  function setIdle(next) {
    if (idle === next) return;
    idle = next;
    onChange?.(idle);
  }
  function restart() {
    if (timer !== void 0) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = void 0;
      setIdle(true);
    }, timeoutMs);
  }
  function onActivity() {
    const now = Date.now();
    if (now - lastActivity < THROTTLE_MS) return;
    lastActivity = now;
    restart();
    setIdle(false);
  }
  const listenerOptions = { capture: true, passive: true };
  for (const type of ACTIVITY_EVENTS) target.addEventListener(type, onActivity, listenerOptions);
  restart();
  return {
    isIdle: () => idle,
    reset() {
      if (destroyed) return;
      lastActivity = Date.now();
      restart();
      setIdle(false);
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      if (timer !== void 0) clearTimeout(timer);
      for (const type of ACTIVITY_EVENTS) target.removeEventListener(type, onActivity, listenerOptions);
    }
  };
}

// src/react/reloadController.ts
var DEFAULT_KEEP_EDITING_MS = 12e4;
var SAME_BUILD_RETRY_WINDOW_MS = 10 * 6e4;
var NORMAL_CONFIRM_HEADLINE = "Save or discard your changes to update";
var URGENT_CONFIRM_HEADLINE = "An update is required. Save or discard your changes to continue.";
var UPDATE_REQUIRED_HEADLINE = "This page is out of date";
var NO_PROMPT = Object.freeze({
  phase: "none",
  policy: null,
  secondsRemaining: null,
  guardReasons: Object.freeze([]),
  confirm: null,
  deadline: null,
  secondsToDeadline: null,
  error: null,
  keepEditing: null
});
function createReloadController(options) {
  const { updater, guards, document: doc, reload, idleTimeoutMs, countdownSeconds, storage, storageKey } = options;
  const retry = resolveRetry(options.retry);
  const keepEditingMs = options.keepEditingMs ?? DEFAULT_KEEP_EDITING_MS;
  const log = updater.log;
  const bundleBuildId = updater.getState().bundle.buildId;
  let destroyed = false;
  let reloading = false;
  let dismissed = false;
  let confirm = null;
  let countdownTimer;
  let secondsRemaining = null;
  let countdownDone = false;
  let deadlineTimer;
  let blockedByDeadline = false;
  let required = null;
  let requiredAt = null;
  let requiredGaveUp = null;
  let chunkRetry = null;
  let chunkTimer;
  let keepEditingUntil = null;
  let keepEditingTimer;
  let snapshot = NO_PROMPT;
  const listeners = /* @__PURE__ */ new Set();
  const idle = doc ? createIdleTracker({
    target: doc,
    timeoutMs: idleTimeoutMs,
    onChange(isIdle) {
      if (isIdle) dismissed = false;
      else countdownDone = false;
      evaluate();
    }
  }) : null;
  function pendingManifestId(state) {
    return state.status === "pending" ? state.manifest.buildId : null;
  }
  function readRecord() {
    if (!storage) return void 0;
    let raw;
    try {
      raw = storage.getItem(storageKey);
    } catch {
      return void 0;
    }
    if (raw === null || raw === "") return null;
    try {
      const value = JSON.parse(raw);
      if (value && typeof value.buildId === "string" && Number.isSafeInteger(value.count) && value.count >= 0) {
        return {
          buildId: value.buildId,
          count: value.count,
          from: typeof value.from === "string" ? value.from : null,
          at: typeof value.at === "number" && Number.isFinite(value.at) ? value.at : null
        };
      }
    } catch {
    }
    return { buildId: raw, count: 1, from: null, at: null };
  }
  function reloadsFor(buildId) {
    const record = readRecord();
    if (record === void 0) return Infinity;
    if (!record || record.buildId !== buildId) return 0;
    if (record.from === record.buildId && (record.at === null || Date.now() - record.at > SAME_BUILD_RETRY_WINDOW_MS)) return 0;
    return record.count;
  }
  function recordReload(buildId) {
    const count = reloadsFor(buildId);
    if (!Number.isFinite(count)) return;
    try {
      storage?.setItem(storageKey, JSON.stringify({ buildId, count: count + 1, from: bundleBuildId, at: Date.now() }));
    } catch {
    }
  }
  const landed = readRecord();
  if (landed && landed.from !== landed.buildId) {
    const outcome = {
      result: landed.buildId === bundleBuildId ? "confirmed" : "mismatch",
      expectedBuildId: landed.buildId,
      loadedBuildId: bundleBuildId,
      reloads: landed.count
    };
    if (outcome.result === "confirmed") {
      log("reload-confirmed", { buildId: bundleBuildId, reloads: landed.count });
      try {
        if (storage?.removeItem) storage.removeItem(storageKey);
        else storage?.setItem(storageKey, "");
      } catch {
      }
    } else {
      log("reload-mismatch", { expectedBuildId: landed.buildId, loadedBuildId: bundleBuildId, reloads: landed.count });
      if (landed.count >= retry.maxAttempts) {
        updater.fail("asset-check", { buildId: landed.buildId, attempts: landed.count });
      }
    }
    updater.reportReloadOutcome(outcome);
  }
  function autoReloadSpent(buildId) {
    return reloadsFor(buildId) >= 1;
  }
  function reloadTarget(state) {
    if (state.status === "pending") return state.manifest.buildId;
    if (required) return bundleBuildId;
    if (state.status === "error") return state.buildId;
    return null;
  }
  function performReload(why, target = reloadTarget(updater.getState())) {
    if (reloading || destroyed) return;
    reloading = true;
    stopCountdown();
    stopDeadlineTick();
    stopChunkRetry();
    endKeepEditing();
    const buildId = target;
    if (buildId !== null) recordReload(buildId);
    log("reload", { why, manifestBuildId: buildId });
    publish();
    try {
      reload();
    } catch {
    }
  }
  function startCountdown() {
    secondsRemaining = countdownSeconds;
    countdownDone = false;
    const tick = () => {
      countdownTimer = setTimeout(() => {
        countdownTimer = void 0;
        if (destroyed || secondsRemaining === null) return;
        secondsRemaining -= 1;
        if (secondsRemaining <= 0) {
          countdownDone = true;
          evaluate();
        } else {
          publish();
          tick();
        }
      }, 1e3);
    };
    tick();
  }
  function stopCountdown() {
    if (countdownTimer !== void 0) clearTimeout(countdownTimer);
    countdownTimer = void 0;
    secondsRemaining = null;
  }
  function resetForNoUpdate() {
    stopCountdown();
    stopDeadlineTick();
    countdownDone = false;
    dismissed = false;
    confirm = null;
    blockedByDeadline = false;
    endKeepEditing();
  }
  function urgentDeadline(state) {
    return state.status === "pending" && state.policy === "urgent" ? state.deadline : null;
  }
  function secondsTo(deadline) {
    return deadline === null ? null : Math.max(0, Math.ceil((deadline - Date.now()) / 1e3));
  }
  function scheduleDeadlineTick(deadline) {
    if (deadlineTimer !== void 0) return;
    const left = deadline - Date.now();
    if (left <= 0) return;
    deadlineTimer = setTimeout(() => {
      deadlineTimer = void 0;
      evaluate();
    }, left % 1e3 || 1e3);
  }
  function stopDeadlineTick() {
    if (deadlineTimer !== void 0) clearTimeout(deadlineTimer);
    deadlineTimer = void 0;
  }
  function keepingEditing() {
    if (keepEditingUntil === null) return false;
    if (Date.now() < keepEditingUntil) return true;
    endKeepEditing();
    return false;
  }
  function endKeepEditing() {
    if (keepEditingTimer !== void 0) clearTimeout(keepEditingTimer);
    keepEditingTimer = void 0;
    keepEditingUntil = null;
  }
  function scheduleKeepEditingTick() {
    if (keepEditingTimer !== void 0 || keepEditingUntil === null) return;
    const left = keepEditingUntil - Date.now();
    if (left <= 0) return;
    keepEditingTimer = setTimeout(() => {
      keepEditingTimer = void 0;
      evaluate();
    }, left % 1e3 || 1e3);
  }
  function stopChunkRetry() {
    if (chunkTimer !== void 0) clearTimeout(chunkTimer);
    chunkTimer = void 0;
    chunkRetry = null;
  }
  function evaluate() {
    if (destroyed) return;
    if (required) {
      if (reloading) return;
      const target = reloadTarget(updater.getState());
      const reloads = target === null ? 0 : reloadsFor(target);
      if (target !== null && reloads >= retry.maxAttempts) {
        if (!requiredGaveUp) {
          requiredGaveUp = { buildId: target, attempts: Number.isFinite(reloads) ? reloads : 0 };
          log("update-required-gave-up", requiredGaveUp);
        }
        stopCountdown();
        stopDeadlineTick();
        endKeepEditing();
        if (confirm && !confirm.cancellable) confirm = null;
        if (confirm && !guards.isActive()) {
          performReload("error-reload");
          return;
        }
        publish(confirm ? "confirm" : "error");
        return;
      }
      if (!guards.isActive()) {
        performReload("update-required");
        return;
      }
      stopCountdown();
      stopDeadlineTick();
      if (keepingEditing()) {
        confirm = null;
        scheduleKeepEditingTick();
        publish("urgent");
        return;
      }
      confirm = required;
      publish("confirm");
      return;
    }
    const state = updater.getState();
    if (chunkRetry && chunkTimer === void 0 && !reloading && !guards.isActive()) {
      performReload("chunk-load", chunkRetry.buildId);
      return;
    }
    if (state.status === "error" && !reloading) {
      stopCountdown();
      stopDeadlineTick();
      countdownDone = false;
      dismissed = false;
      blockedByDeadline = false;
      if (confirm && !confirm.cancellable) confirm = null;
      if (confirm && !guards.isActive()) {
        performReload("error-reload");
        return;
      }
      publish(confirm ? "confirm" : "error");
      return;
    }
    const buildId = pendingManifestId(state);
    if (buildId === null && !reloading) resetForNoUpdate();
    const deadline = urgentDeadline(state);
    const deadlinePassed = deadline !== null && Date.now() >= deadline;
    if (deadline === null) {
      stopDeadlineTick();
      if (blockedByDeadline) {
        confirm = null;
        blockedByDeadline = false;
      }
      endKeepEditing();
    }
    const editing = deadline !== null && keepingEditing();
    const decision = decide({
      update: state.status === "pending" ? state.policy : "none",
      guardsActive: guards.isActive(),
      hidden: doc?.visibilityState === "hidden",
      idle: idle?.isIdle() ?? false,
      countdownDone,
      dismissed,
      confirmOpen: confirm !== null,
      autoReloadSpent: buildId !== null && autoReloadSpent(buildId),
      reloading,
      deadlinePassed,
      keepEditing: editing
    });
    if (decision.reload && state.status === "pending" && !updater.ensureFresh()) {
      log("reload-held-for-recheck", { manifestBuildId: buildId });
      publish(confirm ? "confirm" : state.policy === "urgent" ? "urgent" : "available");
      return;
    }
    if (decision.reload) {
      performReload(
        confirm ? "confirm-cleared" : doc?.visibilityState === "hidden" ? "hidden" : deadlinePassed ? "urgent-deadline" : "countdown"
      );
      return;
    }
    if (decision.block && (!confirm || confirm.cancellable)) {
      confirm = { headline: URGENT_CONFIRM_HEADLINE, cancellable: false, canSave: true };
      blockedByDeadline = true;
      log("urgent-block", { manifestBuildId: buildId });
    }
    if (deadline !== null && !deadlinePassed) scheduleDeadlineTick(deadline);
    if (editing) scheduleKeepEditingTick();
    if (decision.phase === "countdown") {
      if (secondsRemaining === null) startCountdown();
    } else {
      stopCountdown();
      countdownDone = false;
    }
    publish(decision.phase);
  }
  let lastPhase = "none";
  function publish(phase = reloading ? "reloading" : lastPhase) {
    lastPhase = phase;
    const state = updater.getState();
    const next = phase === "none" ? NO_PROMPT : {
      phase,
      policy: required ? "urgent" : state.status === "pending" ? state.policy : null,
      secondsRemaining: phase === "countdown" ? secondsRemaining : null,
      guardReasons: guards.reasons(),
      confirm: phase === "confirm" ? confirm : null,
      deadline: requiredAt ?? urgentDeadline(state),
      secondsToDeadline: secondsTo(requiredAt ?? urgentDeadline(state)),
      error: phase !== "error" ? null : requiredGaveUp ? { reason: "update-required", ...requiredGaveUp } : state.status === "error" ? { reason: state.reason, buildId: state.buildId, attempts: state.attempts } : null,
      keepEditing: phase === "urgent" && keepEditingUntil !== null ? { until: keepEditingUntil, secondsLeft: secondsTo(keepEditingUntil) ?? 0, canSave: !required } : null
    };
    if (sameSnapshot(snapshot, next)) return;
    if (next.phase !== snapshot.phase) log("prompt-phase", { from: snapshot.phase, to: next.phase });
    snapshot = next;
    for (const l of [...listeners]) l();
  }
  const onVisibility = () => {
    if (doc?.visibilityState === "visible") idle?.reset();
    evaluate();
  };
  doc?.addEventListener("visibilitychange", onVisibility);
  const unsubscribeUpdater = updater.subscribe(() => evaluate());
  const unsubscribeGuards = guards.subscribe(() => evaluate());
  evaluate();
  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    reloadNow() {
      const status = updater.getState().status;
      if (destroyed || !required && status !== "pending" && status !== "error") return;
      if (guards.isActive()) {
        endKeepEditing();
        if (!confirm) confirm = { headline: NORMAL_CONFIRM_HEADLINE, cancellable: true, canSave: true };
        evaluate();
        return;
      }
      performReload(status === "error" ? "error-reload" : "reload-now");
    },
    dismiss() {
      if (destroyed) return;
      dismissed = true;
      countdownDone = false;
      idle?.reset();
      evaluate();
    },
    keepEditing() {
      if (destroyed || !confirm) return;
      if (confirm.cancellable) {
        confirm = null;
      } else {
        keepEditingUntil = Date.now() + keepEditingMs;
        if (keepEditingTimer !== void 0) clearTimeout(keepEditingTimer);
        keepEditingTimer = void 0;
        confirm = null;
        blockedByDeadline = false;
        log("keep-editing", { forMs: keepEditingMs, canSave: !required });
      }
      evaluate();
    },
    discardAndReload() {
      const status = updater.getState().status;
      if (destroyed || !required && status !== "pending" && status !== "error") return;
      performReload("discard");
    },
    cancelConfirm() {
      if (!confirm?.cancellable) return;
      confirm = null;
      evaluate();
    },
    block({ headline }) {
      if (destroyed || updater.getState().status !== "pending") return;
      confirm = { headline, cancellable: false, canSave: true };
      evaluate();
    },
    requireUpdate({ headline }) {
      if (destroyed || required) return;
      required = { headline, cancellable: false, canSave: false };
      requiredAt = Date.now();
      blockedByDeadline = false;
      log("update-required", { guardsActive: guards.isActive() });
      evaluate();
    },
    chunkLoadFailed(error) {
      if (destroyed || reloading || chunkRetry) return;
      const state = updater.getState();
      if (state.status === "error") return;
      const buildId = state.status === "pending" ? state.manifest.buildId : state.bundle.buildId;
      const reloads = reloadsFor(buildId);
      log("chunk-load-failed", { error: String(error), buildId, reloads: Number.isFinite(reloads) ? reloads : null });
      if (reloads >= retry.maxAttempts) {
        updater.fail("chunk-load", { buildId, attempts: Number.isFinite(reloads) ? reloads : 0 });
        return;
      }
      chunkRetry = { buildId };
      const delay = reloads === 0 ? 0 : backoffAfter(retry, reloads);
      if (delay === 0) {
        evaluate();
        return;
      }
      log("chunk-load-retry", { buildId, delayMs: delay });
      chunkTimer = setTimeout(() => {
        chunkTimer = void 0;
        evaluate();
      }, delay);
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      stopCountdown();
      stopDeadlineTick();
      stopChunkRetry();
      endKeepEditing();
      idle?.destroy();
      doc?.removeEventListener("visibilitychange", onVisibility);
      unsubscribeUpdater();
      unsubscribeGuards();
      listeners.clear();
    }
  };
}
function sameSnapshot(a, b) {
  return a.phase === b.phase && a.policy === b.policy && a.secondsRemaining === b.secondsRemaining && a.confirm === b.confirm && a.deadline === b.deadline && a.secondsToDeadline === b.secondsToDeadline && a.error?.reason === b.error?.reason && a.error?.buildId === b.error?.buildId && a.error?.attempts === b.error?.attempts && a.keepEditing?.until === b.keepEditing?.until && a.keepEditing?.secondsLeft === b.keepEditing?.secondsLeft && a.keepEditing?.canSave === b.keepEditing?.canSave && a.guardReasons.length === b.guardReasons.length && a.guardReasons.every((r, i) => r === b.guardReasons[i]);
}

// src/react/UpdateProvider.tsx
import { useEffect, useMemo, useState } from "react";

// src/react/browserReload.ts
function browserReload() {
  window.location.reload();
}

// src/react/context.ts
import { createContext, useContext } from "react";
var UpdateContext = createContext(null);
function useUpdateContext() {
  return useContext(UpdateContext);
}

// src/react/guards.ts
function createGuardRegistry() {
  const active = /* @__PURE__ */ new Map();
  const listeners = /* @__PURE__ */ new Set();
  return {
    set(id, isActive, reason) {
      const had = active.has(id);
      if (isActive) {
        if (had && active.get(id) === reason) return;
        active.set(id, reason);
      } else {
        if (!had) return;
        active.delete(id);
      }
      for (const l of [...listeners]) l();
    },
    isActive: () => active.size > 0,
    // Two guards can give the same reason (one screen, two saves), and the
    // dialog lists each reason once.
    reasons: () => [...new Set([...active.values()].filter((r) => typeof r === "string" && r.length > 0))],
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    }
  };
}

// src/react/UpdateProvider.tsx
import { jsx } from "react/jsx-runtime";
var DEFAULT_IDLE_TIMEOUT_MS = 1e4;
var DEFAULT_COUNTDOWN_SECONDS = 30;
function sessionStorageOrNull() {
  try {
    return typeof window !== "undefined" && window.sessionStorage ? window.sessionStorage : null;
  } catch {
    return null;
  }
}
function UpdateProvider(props) {
  const { children } = props;
  const [guards] = useState(createGuardRegistry);
  const [mountProps] = useState(() => props);
  const [live, setLive] = useState(null);
  useEffect(() => {
    const p = mountProps;
    const owned = !p.updater;
    const updater = p.updater ?? createAppUpdater({
      bundle: p.bundle,
      versionUrl: p.versionUrl,
      channelName: p.channelName,
      pollIntervalMs: p.pollIntervalMs,
      debug: p.debug,
      urgentDeadlineMs: p.urgentDeadlineMs,
      retry: p.retry,
      indexUrl: p.indexUrl
    });
    const controller = createReloadController({
      updater,
      guards,
      document: typeof document !== "undefined" ? document : null,
      reload: p.reload ?? browserReload,
      idleTimeoutMs: p.idleTimeoutMs ?? DEFAULT_IDLE_TIMEOUT_MS,
      countdownSeconds: p.countdownSeconds ?? DEFAULT_COUNTDOWN_SECONDS,
      storage: sessionStorageOrNull(),
      storageKey: `${p.channelName ?? DEFAULT_CHANNEL_NAME}:auto-reload`,
      retry: p.retry,
      keepEditingMs: p.keepEditingMs
    });
    const onPreloadError = (event) => {
      event.preventDefault();
      controller.chunkLoadFailed(event.payload);
    };
    const listening = p.handleChunkLoadErrors === true && typeof window !== "undefined";
    if (listening) window.addEventListener("vite:preloadError", onPreloadError);
    setLive({ updater, controller });
    return () => {
      if (listening) window.removeEventListener("vite:preloadError", onPreloadError);
      controller.destroy();
      if (owned) updater.destroy();
      setLive(null);
    };
  }, [guards, mountProps]);
  const value = useMemo(
    () => ({
      bundle: mountProps.bundle,
      countdownSeconds: mountProps.countdownSeconds ?? DEFAULT_COUNTDOWN_SECONDS,
      guards,
      updater: live?.updater ?? null,
      controller: live?.controller ?? null
    }),
    [guards, live, mountProps]
  );
  return /* @__PURE__ */ jsx(UpdateContext.Provider, { value, children });
}

// src/react/hooks.ts
import { useCallback, useEffect as useEffect2, useId, useMemo as useMemo2, useState as useState2, useSyncExternalStore } from "react";
var noopSubscribe = () => () => {
};
function useReloadGuard(active, reason) {
  const ctx = useUpdateContext();
  const guards = ctx?.guards;
  const id = useId();
  useEffect2(() => {
    if (!guards) return;
    guards.set(id, active, reason);
    return () => guards.set(id, false);
  }, [guards, id, active, reason]);
}
function useUpdateState() {
  const ctx = useUpdateContext();
  const updater = ctx?.updater ?? null;
  const fallback = useMemo2(() => ctx ? { status: "current", bundle: ctx.bundle } : null, [ctx]);
  const subscribe = useCallback((cb) => updater ? updater.subscribe(cb) : noopSubscribe(), [updater]);
  const get = useCallback(() => updater ? updater.getState() : fallback, [updater, fallback]);
  return useSyncExternalStore(subscribe, get, get);
}
function useUpdatePrompt() {
  const controller = useUpdateContext()?.controller ?? null;
  const subscribe = useCallback((cb) => controller ? controller.subscribe(cb) : noopSubscribe(), [controller]);
  const get = useCallback(() => controller ? controller.getSnapshot() : NO_PROMPT, [controller]);
  return useSyncExternalStore(subscribe, get, get);
}
function useUpdateActions() {
  const ctx = useUpdateContext();
  const controller = ctx?.controller ?? null;
  const updater = ctx?.updater ?? null;
  return useMemo2(
    () => ({
      reloadNow: () => controller?.reloadNow(),
      dismiss: () => controller?.dismiss(),
      keepEditing: () => controller?.keepEditing(),
      discardAndReload: () => controller?.discardAndReload(),
      cancelConfirm: () => controller?.cancelConfirm(),
      checkNow: () => updater ? updater.checkNow() : Promise.resolve(),
      requireUpdate: (request) => {
        if (!controller) return false;
        controller.requireUpdate(request);
        return true;
      }
    }),
    [controller, updater]
  );
}
function useIdleDetector({ timeoutMs = DEFAULT_IDLE_TIMEOUT_MS, enabled = true } = {}) {
  const [isIdle, setIsIdle] = useState2(false);
  useEffect2(() => {
    if (!enabled || typeof document === "undefined") {
      setIsIdle(false);
      return;
    }
    const tracker = createIdleTracker({ target: document, timeoutMs, onChange: setIsIdle });
    return () => tracker.destroy();
  }, [enabled, timeoutMs]);
  return { isIdle };
}

export {
  useUpdateContext,
  DEFAULT_KEEP_EDITING_MS,
  SAME_BUILD_RETRY_WINDOW_MS,
  URGENT_CONFIRM_HEADLINE,
  UPDATE_REQUIRED_HEADLINE,
  DEFAULT_IDLE_TIMEOUT_MS,
  DEFAULT_COUNTDOWN_SECONDS,
  UpdateProvider,
  useReloadGuard,
  useUpdateState,
  useUpdatePrompt,
  useUpdateActions,
  useIdleDetector
};
//# sourceMappingURL=chunk-YGDUVY2Q.mjs.map